import {
    KpiSummary,
    KpiMetric,
    SalesAttribution,
    SalesAttributionSeries,
    SalesAttributionDataPoint,
    TopItems,
    TopItemsType,
    TopItem,
    Adjustments,
    AdjustmentBucket,
    SparklinePoint,
    Leaderboard,
    LeaderboardSortBy,
    LeaderboardLocation,
    DateRange,
    SalesDashboardFilters
} from 'model/salesDashboard';
import {
    KpiSummaryDto,
    KpiMetricDto,
    SalesAttributionDto,
    SalesAttributionSeriesDto,
    SalesAttributionDataPointDto,
    TopItemsDto,
    TopItemDto,
    AdjustmentsDto,
    AdjustmentBucketDto,
    SparklinePointDto,
    LeaderboardDto,
    LeaderboardLocationDto,
    DateRangeDto,
    SalesDashboardQueryDto
} from './SalesDashboardDtos';

const convertToDateRange = (dto: DateRangeDto): DateRange => ({
    start: dto.start,
    end: dto.end
});

const convertToKpiMetric = (dto: KpiMetricDto): KpiMetric => ({
    amount: dto.amount,
    avgPrice: dto.avg_price,
    ...(dto.change_pct != null && { changePct: dto.change_pct })
});

export const convertToKpiSummary = (dto: KpiSummaryDto): KpiSummary => ({
    totalSales: convertToKpiMetric(dto.total_sales),
    boarding: convertToKpiMetric(dto.boarding),
    daycare: convertToKpiMetric(dto.daycare),
    grooming: convertToKpiMetric(dto.grooming),
    addons: convertToKpiMetric(dto.addons),
    products: convertToKpiMetric(dto.products),
    customItems: convertToKpiMetric(dto.custom_items)
});

const convertToSalesAttributionDataPoint = (dto: SalesAttributionDataPointDto): SalesAttributionDataPoint => ({
    month: dto.month,
    netSales: dto.net_sales
});

const convertToSalesAttributionSeries = (dto: SalesAttributionSeriesDto): SalesAttributionSeries => ({
    key: dto.key,
    label: dto.label,
    data: dto.data.map(convertToSalesAttributionDataPoint)
});

export const convertToSalesAttribution = (dto: SalesAttributionDto): SalesAttribution => ({
    range: convertToDateRange(dto.range),
    series: dto.series.map(convertToSalesAttributionSeries)
});

const convertToTopItem = (dto: TopItemDto): TopItem => ({
    ranking: dto.ranking,
    name: dto.name,
    sales: dto.sales,
    sold: dto.sold
});

export const convertToTopItems = (dto: TopItemsDto): TopItems => ({
    type: dto.type as TopItemsType,
    range: convertToDateRange(dto.range),
    meta: {
        page: dto.meta.page,
        perPage: dto.meta.per_page,
        totalCount: dto.meta.total_count
    },
    items: dto.items.map(convertToTopItem)
});

const convertToSparklinePoint = (dto: SparklinePointDto): SparklinePoint => ({
    month: dto.month,
    amount: dto.amount
});

const convertToAdjustmentBucket = (dto: AdjustmentBucketDto): AdjustmentBucket => ({
    total: dto.total,
    sparkline: dto.sparkline.map(convertToSparklinePoint),
    compareTotal: dto.compare_total,
    compareSparkline: dto.compare_sparkline.map(convertToSparklinePoint),
    changePct: dto.change_pct
});

export const convertToAdjustments = (dto: AdjustmentsDto): Adjustments => ({
    range: convertToDateRange(dto.range),
    refunds: convertToAdjustmentBucket(dto.refunds),
    chargebacks: convertToAdjustmentBucket(dto.chargebacks),
    forfeitedDeposits: convertToAdjustmentBucket(dto.forfeited_deposits)
});

const convertToLeaderboardLocation = (dto: LeaderboardLocationDto): LeaderboardLocation => ({
    marketplaceId: dto.marketplace_id,
    name: dto.name,
    region: dto.region,
    netSales: dto.net_sales,
    avgOrderValue: dto.avg_order_value,
    salesPerStaff: dto.sales_per_staff,
    activeClients: dto.active_clients,
    newClients: dto.new_clients,
    retentionRate: dto.retention_rate,
    ranking: dto.ranking
});

export const convertToLeaderboard = (dto: LeaderboardDto): Leaderboard => ({
    range: convertToDateRange(dto.range),
    sortBy: dto.sort_by as LeaderboardSortBy,
    locations: dto.locations.map(convertToLeaderboardLocation)
});

export const toSalesDashboardQueryDto = (filters: SalesDashboardFilters): SalesDashboardQueryDto => ({
    start_date: filters.startDate.format('YYYY-MM-DD'),
    end_date: filters.endDate.format('YYYY-MM-DD'),
    ...(filters.compareStartDate !== undefined && {
        compare_start_date: filters.compareStartDate.format('YYYY-MM-DD')
    }),
    ...(filters.compareEndDate !== undefined && { compare_end_date: filters.compareEndDate.format('YYYY-MM-DD') }),
    ...(filters.businessTypes !== undefined && { business_types: filters.businessTypes }),
    ...(filters.marketplaces !== undefined && { marketplaces: filters.marketplaces }),
    ...(filters.regions !== undefined && { regions: filters.regions })
});
